import { createObstructionIndex } from './obstructionIndex.js';
import { isPoleCamera } from './cameraMounts.js';
import { findPoleMount } from './poleMounting.js';
import { withBuildingVisibility } from './buildingVisibility.js';
import { zonesAt, zoneViews } from './coverageGoals.js';
import { isCeilingCamera, cameraMountKind, mountingProfile } from './cameraMounts.js';
import { findCeilingMount } from './ceilingMounting.js';
import { hasPrice } from './costs.js';
import { areaAt, areaEnvironment, orderedSiteAreas, suitableAt, siteSurfaces, hasOutdoor } from './siteAreas.js';
import { DEG, clamp, fmt, angDiff, REQS, reqByKey, modelById } from './constants.js';
import { camGeom, cameraViews, camRanges, groundBand, pxAt, rangeFor, inCameraFrame, cameraFrame } from './optics.js';
import { findWallMount } from './mounting.js';
import { assessEntries } from './entries.js';
import { assessTasks } from './tasks.js';
import { sceneWalls, sceneIssues } from './siteGeometry.js';
import { generalKey, threshold, needsFace, detailLevels } from './standards.js';
import { insideSite, siteBBox, ptInPoly, polyCentroid, blocked, wallsNear, wallIntersection } from './geometry.js';
import { formatArea, formatLength } from './units.js';

const MAX_CELLS = 420000;
// Per-field helpers stay outside the field, which must remain structured-cloneable.
const fieldObstructions=new WeakMap(),fieldFrames=new WeakMap();
function fieldBlocked(F,x,y,z){
 const c=F.cam,entry=fieldObstructions.get(F);
 if(!entry)return blocked(c.x,c.y,c.h,x,y,z,F.walls);
 return entry.scope.anySegment(c.x,c.y,c.h,x,y,z,wall=>{entry.single[0]=wall;return blocked(c.x,c.y,c.h,x,y,z,entry.single);},()=>blocked(c.x,c.y,c.h,x,y,z,F.walls));
}
const finishDiagnostics=(options,index)=>{if(options?.diagnostics)Object.assign(options.diagnostics,index.diagnostics());};

// Line-of-sight results reused between evaluations. A camera-to-sample sight
// line is blocked or clear depending only on the camera position, the sample
// position and height, and the walls near the camera. Each camera view keeps a
// byte per sample in its window: 1 face line known, 2 face line blocked,
// 4 plate line known, 8 plate line blocked. The key covers every input of that
// test, so a reused answer is the answer the test would give again.
const fieldVisibility=new WeakMap();
const VISIBILITY_BYTES=48*1024*1024;
function digest128(text){
 let a=1779033703,b=3144134277,c=1013904242,d=2773480762;
 for(let i=0;i<text.length;i++){const k=text.charCodeAt(i);a=b^Math.imul(a^k,597399067);b=c^Math.imul(b^k,2869860233);c=d^Math.imul(c^k,951274213);d=a^Math.imul(d^k,2716044179);}
 a=Math.imul(c^(a>>>18),597399067);b=Math.imul(d^(b>>>22),2869860233);c=Math.imul(a^(c>>>17),951274213);d=Math.imul(b^(d>>>19),2716044179);
 a^=b^c^d;b^=a;c^=a;d^=a;
 return [a,b,c,d].map(v=>(v>>>0).toString(16).padStart(8,'0')).join('');
}
/** Let one camera view read and record sight-line bytes held by the caller,
 * indexed by the flat sample number passed to sampleFromCam as its column. */
export function shareSightLines(field, bits) { fieldVisibility.set(field, { bits, c0: 0, r0: 0, width: 0 }); }
/** Attach reusable sight-line bytes to one camera view for this evaluation. */
function attachVisibility(state,S,G,field,window){
 if(!state)return;
 const cells=(window.c1-window.c0+1)*(window.r1-window.r0+1);
 if(state.bytes+cells>VISIBILITY_BYTES)return;
 let walls='';
 for(const wall of field.walls){let id=state.walls.get(wall);if(id===undefined){id=digest128(JSON.stringify(wall));state.walls.set(wall,id);}walls+=id;}
 const c=field.cam,key=digest128(JSON.stringify([c.x,c.y,c.h,S.opt.hFace,S.opt.hPlate,G.x0,G.y0,G.step,G.cols,G.rows,window.c0,window.c1,window.r0,window.r1,field.walls.length])+walls);
 // The same view can only be attached once; two cameras at one position share the answers.
 let bits=state.next.get(key)||state.previous?.get(key);
 if(!(bits instanceof Uint8Array)||bits.length!==cells){bits=new Uint8Array(cells);state.misses++;}else state.hits++;
 if(!state.next.has(key)){state.next.set(key,bits);state.bytes+=cells;}
 fieldVisibility.set(field,{bits,c0:window.c0,r0:window.r0,width:window.c1-window.c0+1});
}

/** Rasterise the site into a sample grid, tagging each cell with the grade it
 *  is held to — a zone's requirement where one covers it, the general target
 *  everywhere else. */
export function buildGrid(S, stepIn, offset = 0.5, maxCells = MAX_CELLS, options = {}) {
  S = withBuildingVisibility(S);
  const b = siteBBox(S);
  if (!b) return null;
  let step = stepIn || S.opt.step;
  const w = b.x1 - b.x0, h = b.y1 - b.y0;
  let cols = Math.max(1, Math.ceil(w / step));
  let rows = Math.max(1, Math.ceil(h / step));
  while (cols * rows > maxCells) {
    step *= 1.35;
    cols = Math.ceil(w / step);
    rows = Math.ceil(h / step);
  }
  const n = cols * rows;
  const G = {
    step, cols, rows, n, x0: b.x0 + step * offset, y0: b.y0 + step * offset,
    sx: new Float32Array(n), sy: new Float32Array(n),
    inSite: new Uint8Array(n),
    outdoor: new Uint8Array(n),
    area: new Int16Array(n).fill(-1),
    zone: new Int16Array(n).fill(-1),
    req: new Uint8Array(n), baseReq: new Int16Array(n).fill(-1), zonesAt: Array(n),
    live: 0
  };
  const gen = Math.max(0, REQS.findIndex(r => r.k === generalKey(S)));
  const walls = sceneWalls(S),obstructions=createObstructionIndex(walls,{mode:options.obstructionMode});
  const siteAreas = orderedSiteAreas(S);
  const areaIds = new Map(S.areas.map((a, i) => [a, i]));
  // strictest requirement wins where zones overlap
  const zsorted = S.zones
    .map((z, i) => ({ z, i }))
    .sort((a, b2) => threshold(S, a.z.req) - threshold(S, b2.z.req) || (a.z.id || '').localeCompare(b2.z.id || ''));

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const i = r * cols + c;
      const x = G.x0 + c * step, y = G.y0 + r * step;
      G.sx[i] = x; G.sy[i] = y; G.req[i] = gen;
      if (!insideSite(S, x, y)) continue;
      // Wall footprints are structure, not floor that a camera must cover.
      if (obstructions.queryPoint(x,y,.1).some(wall => wallIntersection(x, y, 0.1, x, y, 0.1, wall))) continue;
      G.inSite[i] = 1; G.live++;
      const area = areaAt(S, x, y, siteAreas);
      G.area[i] = areaIds.get(area) ?? -1;
      G.outdoor[i] = Number(areaEnvironment(area || {}) === 'outdoor');
      if (area?.req) { const requirement = REQS.findIndex(q => q.k === area.req); if (requirement >= 0) G.req[i] = requirement; }
      const memberships = [];
      const base = G.req[i];
      for (const { z, i: zi } of zsorted) {
        if (ptInPoly(x, y, z.pts)) {
          memberships.push(zi); G.zone[i] = zi;
          if (!area?.req || threshold(S, z.req) >= threshold(S, REQS[G.req[i]].k)) G.req[i] = REQS.findIndex(q => q.k === z.req);
        }
      }
      G.zonesAt[i] = memberships;
      G.baseReq[i] = area?.req || !memberships.length ? base : -1;
    }
  }
  finishDiagnostics(options,obstructions);
  return G;
}

/** Everything about one camera that does not change per sample. */
export function camField(S, cam, singleG, obstructionIndex) {
  if (!singleG) {
    const views = cameraViews(S, cam);
    if (views.length > 1) {
      const fields = views.map(v => camField(S, v.cam, v.g, obstructionIndex));
      return { ...fields[0], cam, fields, maxG: Math.max(...fields.map(f => f.maxG)) };
    }
    if (views[0]?.module) return camField(S, views[0].cam, views[0].g, obstructionIndex);
  }
  const g = singleG || camGeom(S, cam);
  if (!g) return null;
  const t = S.opt.t, o = S.opt;
  const rDet = rangeFor(g, Math.min(t.detect, ...detailLevels(S).slice(1).map(l => l.px)));
  const dh = cam.h - o.hFace;
  const maxG = Math.sqrt(Math.max(0.01, rDet * rDet - dh * dh));
  const field = {
    cam, g, maxG, rDet, levels: detailLevels(S),
    bandF: groundBand(cam, g, o.hFace),
    bandP: groundBand(cam, g, o.hPlate),
    walls: wallsNear(S, cam.x, cam.y, maxG + 1.5)
  };
  fieldFrames.set(field, cameraFrame(cam, g));
  // Every visibility ray starts at the camera, so its walls are indexed by direction.
  if(obstructionIndex)fieldObstructions.set(field,{scope:obstructionIndex.forRays(field.walls,cam.x,cam.y,cam.h),single:[null]});
  return field;
}

/** Does this camera see this point, and at what grade? */
export function sampleFromCam(S, F, x, y, column, row) {
  if (F.fields) {
    const samples = F.fields.map(f => sampleFromCam(S, f, x, y, column, row)).filter(Boolean);
    if (!samples.length) return null;
    const best = samples.slice().sort((a, b) => b.px - a.px)[0];
    return { ...best, lvl: Math.max(...samples.map(s => s.lvl)), faceOk: samples.some(s => s.faceOk), plateOk: samples.some(s => s.plateOk),
      platePx: Math.max(...samples.map(s => s.platePx)), facePx: Math.max(0, ...samples.filter(s => s.vAngF <= S.opt.aFace).map(s => s.px)) };
  }
  const cam = F.cam, o = S.opt, t = o.t, g = F.g;
  const dx = x - cam.x, dy = y - cam.y;
  const d = Math.hypot(dx, dy);
  if (d > F.maxG) return null;

  const frame = fieldFrames.get(F);
  const inFace = frame ? frame(x, y, o.hFace) : inCameraFrame(cam, g, x, y, o.hFace);
  const inPlate = frame ? frame(x, y, o.hPlate) : inCameraFrame(cam, g, x, y, o.hPlate);
  if (!inFace && !inPlate) return null;
  const bearing = Math.atan2(dy, dx) * DEG;

  let lvl = 0, px = 0, platePx = 0, faceOk = false, plateOk = false, vAngF = 0;
  // With a grid position, sight lines already tested for this view are reused.
  const seen = column === undefined ? undefined : fieldVisibility.get(F);
  const cell = seen ? (row - seen.r0) * seen.width + (column - seen.c0) : 0;
  const lineBlocked = (height, known, isBlocked) => {
    if (!seen) return fieldBlocked(F, x, y, height);
    const bits = seen.bits[cell];
    if (bits & known) return (bits & isBlocked) !== 0;
    const result = fieldBlocked(F, x, y, height);
    seen.bits[cell] = bits | known | (result ? isBlocked : 0);
    return result;
  };

  if (inFace && !lineBlocked(o.hFace, 1, 2)) {
    const r = Math.hypot(d, cam.h - o.hFace);
    px = pxAt(g, r);
    vAngF = Math.atan2(Math.max(cam.h - o.hFace, 0), Math.max(d, 0.01)) * DEG;
    for (let i = 1; i < F.levels.length; i++) if (px >= F.levels[i].px && (!needsFace(F.levels[i].k) || vAngF <= o.aFace)) lvl = i;
    faceOk = px >= t.identify && vAngF <= o.aFace;
  }
  if (inPlate && !lineBlocked(o.hPlate, 4, 8)) {
    const rp = Math.hypot(d, cam.h - o.hPlate);
    const pp = pxAt(g, rp);
    platePx = pp;
    const vAngP = Math.atan2(Math.max(cam.h - o.hPlate, 0), Math.max(d, 0.01)) * DEG;
    plateOk = pp >= t.plate && vAngP <= o.aPlateT;
  }
  if (lvl === 0 && !plateOk) return null;
  return { lvl, px, platePx, faceOk, plateOk, d, vAngF, bearing };
}

export function meetsReq(reqIdx, lvl, faceOk, plateOk, S, px = 0, downAngle = 0, facePx) {
  const k = REQS[reqIdx].k;
  if (k === 'plate') return plateOk;
  if (S) return (needsFace(k) ? facePx ?? (downAngle <= S.opt.aFace ? px : 0) : px) >= threshold(S, k);
  if (k === 'identify') return faceOk;
  return lvl >= REQS[reqIdx].lvl;
}

/** A plate needs the camera roughly square to the direction of travel, not just
 *  enough pixels — so the yaw rule only applies inside a plate zone, which is
 *  the only place we know which way traffic runs. */
export function plateYawOk(S, G, i, bearing) {
  const zi = G.zone[i];
  if (zi < 0 || S.zones[zi].req !== 'plate') return true;
  const ax = S.zones[zi].axis || 0;
  return Math.min(angDiff(bearing, ax), angDiff(bearing, ax + 180)) <= S.opt.aPlateI;
}

export function evaluate(S, G, options = {}) {
  S = withBuildingVisibility(S);
  S = { ...S, _sceneWalls: sceneWalls(S) };
  const n = G.n;
  const cams = S.cams;
  const R = {
    G, cams,
    lvl: new Uint8Array(n),
    px: new Float32Array(n),
    platePx: new Float32Array(n),
    nCam: new Uint16Array(n),
    nMeet: new Uint16Array(n), baseMeet: new Uint16Array(n),
    zoneMeet: S.zones.map(() => new Uint16Array(n)),
    flags: new Uint8Array(n),           // 1 face · 2 plate · 4 requirement met
    camHits: new Int32Array(cams.length),
    camBest: new Int32Array(cams.length),
    camUnique: new Int32Array(cams.length),
    camMeets: new Int32Array(cams.length),
    camZones: cams.map(() => new Int32Array(S.zones.length)),
    cell: G.step * G.step
  };
  const uniqueOwner = new Int16Array(n).fill(-1), bestOwner = new Int16Array(n).fill(-1);
  const obstructions=createObstructionIndex(S._sceneWalls,{mode:options.obstructionMode});
  const fields = cams.map(c => camField(S, c, undefined, obstructions));
  // options.visibility = { previous: Map } on the way in; .next holds this run's entries on the way out.
  const reuse = options.visibility ? { previous: options.visibility.previous, next: new Map(), walls: new Map(), bytes: 0, hits: 0, misses: 0 } : null;

  for (let ci = 0; ci < cams.length; ci++) {
    const F = fields[ci];
    if (!F) continue;
    const c0 = clamp(Math.floor((F.cam.x - F.maxG - G.x0) / G.step), 0, G.cols - 1);
    const c1 = clamp(Math.ceil((F.cam.x + F.maxG - G.x0) / G.step), 0, G.cols - 1);
    const r0 = clamp(Math.floor((F.cam.y - F.maxG - G.y0) / G.step), 0, G.rows - 1);
    const r1 = clamp(Math.ceil((F.cam.y + F.maxG - G.y0) / G.step), 0, G.rows - 1);
    if (reuse) for (const view of F.fields || [F]) attachVisibility(reuse, S, G, view, { c0, c1, r0, r1 });
    for (let r = r0; r <= r1; r++) {
      for (let c = c0; c <= c1; c++) {
        const i = r * G.cols + c;
        if (!G.inSite[i]) continue;
        const s = sampleFromCam(S, F, G.sx[i], G.sy[i], c, r);
        if (!s) continue;
        const plateOk = s.plateOk && plateYawOk(S, G, i, s.bearing);
        if (s.lvl === 0 && !plateOk) continue;
        R.camHits[ci]++;
        if (s.lvl > R.lvl[i]) R.lvl[i] = s.lvl;
        if (s.px > R.px[i]) { R.px[i] = s.px; bestOwner[i] = ci; }
        if (s.platePx > R.platePx[i]) R.platePx[i] = s.platePx;
        R.nCam[i]++;
        if (s.faceOk) R.flags[i] |= 1;
        if (plateOk) R.flags[i] |= 2;
        if (G.baseReq[i] >= 0 && meetsReq(G.baseReq[i], s.lvl, s.faceOk, s.plateOk, S, s.px, s.vAngF, s.facePx)) R.baseMeet[i]++;
        for (const zi of zonesAt(G, i)) {
          const z = S.zones[zi], ax = z.axis || 0;
          const yaw = z.req !== 'plate' || Math.min(angDiff(s.bearing, ax), angDiff(s.bearing, ax + 180)) <= S.opt.aPlateI;
          if (meetsReq(REQS.findIndex(q => q.k === z.req), s.lvl, s.faceOk, s.plateOk && yaw, S, s.px, s.vAngF, s.facePx)) {
            R.zoneMeet[zi][i]++; R.camZones[ci][zi]++;
          }
        }
        if (meetsReq(G.req[i], s.lvl, s.faceOk, plateOk, S, s.px, s.vAngF, s.facePx)) {
          uniqueOwner[i] = R.nMeet[i] === 0 ? ci : -2;
          R.nMeet[i]++; R.camMeets[ci]++;

        }
      }
    }
  }

  const byLevel = detailLevels(S).map(() => 0), viewCounts = [0, 0, 0, 0];
  let met = 0, redundant = 0, excess = 0, faceCells = 0, plateCells = 0;
  for (let i = 0; i < n; i++) {
    if (!G.inSite[i]) continue;
    if (uniqueOwner[i] >= 0) R.camUnique[uniqueOwner[i]]++;
    if (bestOwner[i] >= 0) R.camBest[bestOwner[i]]++;
    byLevel[R.lvl[i]]++;
    if (R.flags[i] & 1) faceCells++;
    if (R.flags[i] & 2) plateCells++;
    if ((G.baseReq[i] < 0 || R.baseMeet[i] > 0) && zonesAt(G, i).every(zi => R.zoneMeet[zi][i] >= zoneViews(S.zones[zi], S.planning))) {
      met++; R.flags[i] |= 4;
    }
    if (R.nMeet[i] >= 2) redundant++;
    const redundantZone = zonesAt(G, i).some(zi => R.zoneMeet[zi][i] > Math.max(S.zones[zi].views || 1, S.planning?.redundancy && ['identify', 'plate'].includes(S.zones[zi].req) ? 2 : 1));
    if ((G.baseReq[i] >= 0 && R.baseMeet[i] > 1) || redundantZone) excess++;
    viewCounts[Math.min(3, R.nMeet[i])]++;
  }

  R.stats = {
    live: G.live, cell: R.cell, area: G.live * R.cell, byLevel,
    coveredPct: G.live ? (G.live - byLevel[0]) / G.live * 100 : 0,
    metPct: G.live ? met / G.live * 100 : 0,
    blindArea: byLevel[0] * R.cell,
    faceArea: faceCells * R.cell,
    plateArea: plateCells * R.cell,
    excessOverlapPct: G.live ? excess / G.live * 100 : 0,
    redundantPct: G.live ? redundant / G.live * 100 : 0, viewCounts
  };

  R.zones = S.zones.map((z, zi) => {
    let total = 0, ok = 0, red = 0, minPx = Infinity;
    for (let i = 0; i < n; i++) {
      if (!G.inSite[i] || !zonesAt(G, i).includes(zi)) continue;
      total++;
      if (R.zoneMeet[zi][i] >= zoneViews(z, S.planning)) ok++;
      if (R.zoneMeet[zi][i] >= 2) red++;
      minPx = Math.min(minPx, z.req === 'plate' ? R.platePx[i] : R.px[i]);
    }
    return {
      z, zi, total, ok, red,
      area: total * R.cell,
      pct: total ? ok / total * 100 : 0,
      minPx: Number.isFinite(minPx) ? minPx : 0
    };
  });

  const areaCounts = S.areas.map(() => ({ total: 0, met: 0 })), environments = { indoor: { total: 0, met: 0 }, outdoor: { total: 0, met: 0 } };
  for (let i = 0; i < n; i++) if (G.inSite[i]) {
    const met = Number(!!(R.flags[i] & 4)), a = areaCounts[G.area?.[i]];
    if (a) { a.total++; a.met += met; }
    const e = environments[G.outdoor?.[i] ? 'outdoor' : 'indoor']; e.total++; e.met += met;
  }
  const combined = hasOutdoor(S), visibleAreas = new Map(siteSurfaces(S).layers.map(l => [l.area.id, l.visibleArea]));
  R.areas = S.areas.map((a, i) => ({ id: a.id, name: a.name, req: a.req || generalKey(S), goal: combined || !!a.req, visibleArea: visibleAreas.get(a.id) || 0, environment: areaEnvironment(a), samples: areaCounts[i].total, area: areaCounts[i].total * R.cell, pct: areaCounts[i].total ? areaCounts[i].met / areaCounts[i].total * 100 : 0 })).filter((a, i) => S.areas[i].kind === 'floor');
  R.environments = Object.entries(environments).map(([environment, r]) => ({ environment, area: r.total * R.cell, samples: r.total, pct: r.total ? r.met / r.total * 100 : 0 })).filter(r => r.samples || (combined && r.environment === 'outdoor'));
  R.blind = findBlind(R);
  R.entries = assessEntries(S);
  R.tasks = assessTasks(S);
  R.findings = audit(S, R);
  finishDiagnostics(options,obstructions);
  if (reuse) Object.assign(options.visibility, { next: reuse.next, hits: reuse.hits, misses: reuse.misses });
  return R;
}

/** Connected blind regions, largest first — a hundred scattered cells and one
 *  seven-square-metre hole are very different problems. */
export function findBlind(R) {
  const G = R.G, seen = new Uint8Array(G.n), out = [], stack = [];
  for (let i = 0; i < G.n; i++) {
    if (seen[i] || !G.inSite[i] || R.lvl[i] !== 0) continue;
    let count = 0, sx = 0, sy = 0;
    stack.length = 0; stack.push(i); seen[i] = 1;
    while (stack.length) {
      const j = stack.pop();
      count++; sx += G.sx[j]; sy += G.sy[j];
      const c = j % G.cols, r = (j - c) / G.cols;
      for (const [dc, dr] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
        const nc = c + dc, nr = r + dr;
        if (nc < 0 || nr < 0 || nc >= G.cols || nr >= G.rows) continue;
        const k = nr * G.cols + nc;
        if (seen[k] || !G.inSite[k] || R.lvl[k] !== 0) continue;
        seen[k] = 1; stack.push(k);
      }
    }
    out.push({ area: count * R.cell, x: G.sx[i], y: G.sy[i], centroid: { x: sx / count, y: sy / count }, cells: count });
  }
  return out.sort((a, b) => b.area - a.area);
}

/** Turn the numbers into things somebody can act on. */
export function audit(S, R) {
  const f = [], o = S.opt, st = R.stats;
  for (const issue of sceneIssues(S)) f.push({ sev: 'serious', t: 'Check scene geometry', d: issue });
  for (const result of R.tasks || []) if (!result.complete) f.push({ sev: 'serious', t: `${result.task.name}: capture task incomplete`, d: `${result.met}/${result.total} approach lanes meet the required views and capture interval. ${result.lanes.find(l => !l.complete)?.checks.find(c => !c.met)?.reason || 'Add an eligible view.'}` });

  if (!R.cams.length) {
    f.push({ sev: 'warning', t: 'No cameras placed', d: 'Add cameras by hand, or hit Auto-plan and let the solver propose a layout.' });
    return f;
  }
  if (st.blindArea > st.area * 0.02 && R.blind.length) {
    const big = R.blind[0];
    f.push({
      sev: st.blindArea > st.area * 0.1 ? 'critical' : 'serious',
      t: `${formatArea(st.blindArea, S, 1)} sees nothing at all`,
      d: `${R.blind.length} separate blind ${R.blind.length === 1 ? 'pocket' : 'pockets'}; the largest is ${formatArea(big.area, S, 1)}. Click to jump to it.`,
      go: { x: big.x, y: big.y }
    });
  }
  for (const zr of R.zones) {
    if (!zr.total) {
      f.push({
        sev: 'warning',
        t: `Zone "${zr.z.name}" has no floor under it`,
        d: 'The zone sits outside every site area, so nothing is being measured inside it.',
        go: polyCentroid(zr.z.pts)
      });
      continue;
    }
    if (zr.pct < 99.5) {
      f.push({
        sev: zr.pct < 60 ? 'critical' : 'serious',
        t: `"${zr.z.name}" is ${fmt(zr.pct, 0)}% to ${reqByKey(zr.z.req).k}`,
        d: `${formatArea(zr.area * (1 - zr.pct / 100), S, 1)} of this zone falls short of ${reqByKey(zr.z.req).label.toLowerCase()}.`,
        go: polyCentroid(zr.z.pts)
      });
    }
  }
  for (const entry of R.entries || []) if (!entry.complete) {
    const point = entry.samples.find(p => !p.cameras.length);
    f.push({ sev: 'serious', t: `${entry.name} lacks frontal identification`, d: `${entry.met}/${entry.total} entry samples have a clear face view. Aim from inside towards the opening, with enough detail and a shallow viewing angle.`, go: { ...point, req: 'identify' } });
  }
  R.cams.forEach((cam, i) => {
    const g = camGeom(S, cam);
    if (!g) { f.push({ sev: 'serious', t: `${cam.name} is missing model specifications`, d: 'Assign an available camera model before relying on its coverage or cost.', go: cam }); return; }
    if (!(g.m.cost > 0)) f.push({ sev: 'warning', t: `${cam.name} has no entered price`, d: 'Its hardware cost is unknown. Add a dated quote before using the total as an installation budget.', go: cam });
    if (!suitableAt(S, g.m, cam.x, cam.y, cam.h)) f.push({ sev: 'serious', t: `${cam.name}: outdoor suitability not confirmed`, d: 'This camera is mounted outdoors. Choose hardware marked Outdoor or Indoor / outdoor in the library. Geometric coverage alone does not establish weather suitability.', go: cam });
    if (g.m.pano && !g.m.details?.sensorModules?.length) f.push({ sev: 'warning', t: `${cam.name}: approximate panoramic optics`, d: 'Enter individual sensor or rectilinear output profiles before relying on this device for automatic planning. Native fisheye distortion is an estimate; calibrated dewarping and stitched panoramic geometry are not modeled.', go: cam });
    if (cam.profileId && !g.m.details?.opticalProfiles?.some(p => p.id === cam.profileId)) f.push({ sev: 'serious', t: `${cam.name}: saved optical profile is missing`, d: 'Choose a valid output profile; the displayed fallback has not been confirmed.', go: cam });
    if (findWallMount(S, cam)?.wall.mountRule === 'prohibited') f.push({ sev: 'serious', t: `${cam.name} is on a prohibited mounting section`, d: 'Move the camera to an allowed wall section or revise the mounting policy.', go: cam });
    if (!mountingProfile(g.m).kinds.includes(cameraMountKind(g.m, cam))) f.push({ sev: 'serious', t: `${cam.name}: unsupported mounting position`, d: 'Choose a mounting position allowed by this model’s mounting profile.', go: cam });
    if (isCeilingCamera(S, cam) && !findCeilingMount(S, cam)) f.push({ sev: 'serious', t: `${cam.name} needs a ceiling mount`, d: 'Attach beneath a modeled roof or upper floor slab. The lens height must match the surface and pendant drop; openings cannot support cameras.', go: cam });
    if (isPoleCamera(S, cam) && !findPoleMount(S, cam)) f.push({ sev: 'serious', t: `${cam.name} needs a pole mount`, d: 'Attach to an enabled exterior pole with enough height for the camera and bracket. Check its bearing, nearby obstructions and outdoor suitability.', go: cam });
    if (!isCeilingCamera(S, cam) && !isPoleCamera(S, cam) && !findWallMount(S, cam)) {
      f.push({ sev: 'serious', t: `${cam.name} needs a wall mount`, d: 'Place this camera beside a solid wall that reaches above its mounting height, or run Auto-plan to replace this layout.', go: cam });
    }
    if (R.camHits[i] === 0) {
      f.push({ sev: 'warning', t: `${cam.name}: no coverage on this level`, d: 'No sampled area on this level is visible at the required detail. Check its aim and obstructions. For views onto the grounds or other floors, check Whole building coverage.', go: cam });
    } else if (R.camUnique[i] === 0 && R.cams.length > 1 && !S.zones.some((z, zi) => z.views > 1 && R.camZones[i][zi] > 0) && !R.entries.some(e => e.samples.some(p => p.cameras.length === 1 && p.cameras[0] === cam.id)) && !R.tasks.some(t => t.lanes.some(l => l.cameras.includes(cam.id) && l.cameras.length <= l.required))) {
      f.push({ sev: 'warning', t: `${cam.name} adds no unique requirement coverage`, d: 'It may provide a second view or partial coverage, but no sampled floor relies on this camera alone to meet its requirement.', go: cam });
    }
    if (cam.tilt > 45) {
      f.push({ sev: 'warning', t: `${cam.name} is tilted ${fmt(cam.tilt, 0)}° down`, d: `Past about ${o.aFace}° you photograph the top of a head. Fine for overwatch, not for a face.`, go: cam });
    }
    if (g.m.lpr && cam.h > 4) {
      f.push({ sev: 'warning', t: `${cam.name} is mounted high for plate work`, d: `Plate cameras want a shallow angle onto the plate. Around ${formatLength(1.5, S)}–${formatLength(3, S)}, set back along the lane, is what usually reads.`, go: cam });
    }
  });
  if (st.metPct >= 99.5 && !f.some(x => x.sev === 'critical' || x.sev === 'serious')) {
    f.unshift({ sev: 'good', t: 'Every requirement is met', d: `${fmt(st.metPct, 1)}% of the site reaches its target grade with ${R.cams.length} cameras.` });
  }
  return f;
}

/** Bill of materials, straight off the model snapshot in the document. */
export function billOfMaterials(S) {
  const bom = new Map();
  for (const c of S.cams) {
    const m = modelById(S, c.modelId);
    if (!m) continue;
    const e = bom.get(m.id) || { m, n: 0 };
    e.n++; bom.set(m.id, e);
  }
  const lines = [...bom.values()];
  return { lines, unknown: lines.reduce((n, l) => n + (!hasPrice(l.m, S.opt.currency) ? l.n : 0), 0), total: lines.reduce((s, l) => s + (hasPrice(l.m, S.opt.currency) ? l.m.cost * l.n : 0), 0) };
}

export { camRanges };
